/**
 * useScore — acompanha totais de pontuação por membership.
 *
 * Subscribe em sessionSnapshot (seed) + scoreUpdate (deltas incrementais)
 * e expõe um map membershipId → total + lista ordenada por total desc.
 * Também expõe participants (displayName, role) vindos do sessionSnapshot
 * pra o ScoreBoard renderizar nomes.
 */

import { useEffect, useMemo, useState } from 'react'

import { useAdapter } from '../adapter'
import type {
  Participant,
  ScoreUpdatePayload,
  SessionSnapshotPayload,
} from '../types/events'

interface ScoreRow {
  membershipId: string
  displayName: string | null
  role: 'master' | 'player'
  total: number
}

interface ScoreState {
  byMembership: Record<string, number>
  participants: Participant[]
}

export function useScore() {
  const adapter = useAdapter()
  const [state, setState] = useState<ScoreState>({
    byMembership: {},
    participants: [],
  })

  useEffect(() => {
    const offSnap = adapter.on(
      'sessionSnapshot',
      (snap: SessionSnapshotPayload) => {
        const byMembership: Record<string, number> = {}
        for (const s of snap.scores ?? []) {
          byMembership[s.membershipId] = s.total
        }
        setState({
          byMembership,
          participants: snap.participants ?? [],
        })
      },
    )

    const offScore = adapter.on('scoreUpdate', (payload: ScoreUpdatePayload) => {
      setState((prev) => {
        const next = { ...prev.byMembership }
        for (const d of payload.deltas ?? []) {
          next[d.membershipId] = (next[d.membershipId] ?? 0) + d.delta
        }
        return { ...prev, byMembership: next }
      })
    })

    const offJoin = adapter.on(
      'participantUpdate',
      (payload: { action: 'joined' | 'left'; participant?: Participant; membershipId?: string }) => {
        setState((prev) => {
          if (payload.action === 'joined' && payload.participant) {
            // evitar duplicação
            const exists = prev.participants.find(
              (p) => p.membershipId === payload.participant!.membershipId,
            )
            if (exists) return prev
            return {
              ...prev,
              participants: [...prev.participants, payload.participant],
            }
          }
          if (payload.action === 'left' && payload.membershipId) {
            return {
              ...prev,
              participants: prev.participants.filter(
                (p) => p.membershipId !== payload.membershipId,
              ),
            }
          }
          return prev
        })
      },
    )

    return () => {
      offSnap()
      offScore()
      offJoin()
    }
  }, [adapter])

  const rows: ScoreRow[] = useMemo(() => {
    const list: ScoreRow[] = state.participants.map((p) => ({
      membershipId: p.membershipId,
      displayName: p.displayName ?? null,
      role: p.role,
      total: state.byMembership[p.membershipId] ?? 0,
    }))
    // Players primeiro, ordenados por total desc. Master por último.
    return list.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'player' ? -1 : 1
      return b.total - a.total
    })
  }, [state.participants, state.byMembership])

  return {
    rows,
    byMembership: state.byMembership,
    totalFor: (membershipId: string | null | undefined) =>
      membershipId ? state.byMembership[membershipId] ?? 0 : 0,
  }
}
